import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, Layout, Save, Rocket } from 'lucide-react';
import { Tooltip } from '../../components/Tooltip';

type LayoutPublishStatus = 'draft' | 'unpublished_changes' | 'published';

interface LayoutEditorHeaderProps {
  siteId: string;
  layoutName: string;
  layoutDescription: string;
  hasUnsavedChanges: boolean;
  isDefault: boolean;
  isSaving: boolean;
  isNewLayout: boolean;
  publishStatus: LayoutPublishStatus;
  isPublishing: boolean;
  onLayoutNameChange: (name: string) => void;
  onLayoutDescriptionChange: (description: string) => void;
  onIsDefaultChange: (isDefault: boolean) => void;
  onSave: () => void;
  onPublish: () => void;
}

export const LayoutEditorHeader: React.FC<LayoutEditorHeaderProps> = ({
  siteId,
  layoutName,
  layoutDescription,
  hasUnsavedChanges,
  isDefault,
  isSaving,
  isNewLayout,
  publishStatus,
  isPublishing,
  onLayoutNameChange,
  onLayoutDescriptionChange,
  onIsDefaultChange,
  onSave,
  onPublish,
}) => {
  return (
    <header className="bg-white border-b border-gray-200 shadow-sm sticky top-0 z-40">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center py-3 sm:py-4 gap-3 sm:gap-0">
          {/* Left side: Back link and layout info */}
          <div className="flex items-center space-x-3 sm:space-x-4 min-w-0">
            <Link
              to={`/sites/${siteId}`}
              className="flex items-center space-x-2 text-gray-600 hover:text-gray-900 transition-colors shrink-0"
            >
              <ArrowLeft size={20} />
              <span className="hidden sm:inline">Back to Site</span>
            </Link>
            <div className="h-6 w-px bg-gray-300 hidden sm:block"></div>
            <div className="flex items-center space-x-2 sm:space-x-3 min-w-0">
              <div className="w-8 h-8 sm:w-10 sm:h-10 rounded-xl bg-gradient-to-r from-purple-500 to-purple-600 flex items-center justify-center shadow-sm shrink-0">
                <Layout className="h-4 w-4 sm:h-5 sm:w-5 text-white" />
              </div>
              <div className="min-w-0">
                <div className="flex items-center space-x-2">
                  <input
                    type="text"
                    value={layoutName}
                    onChange={e => onLayoutNameChange(e.target.value)}
                    placeholder="Layout Name"
                    className="text-base sm:text-xl font-bold text-gray-900 bg-transparent border-none focus:outline-none focus:ring-2 focus:ring-purple-500 rounded px-1 -ml-1 w-32 sm:w-auto"
                  />
                  {hasUnsavedChanges && (
                    <span className="text-xs text-amber-600 bg-amber-50 px-2 py-0.5 rounded shrink-0">
                      Unsaved
                    </span>
                  )}
                  {/* Publish Status Badge */}
                  {!isNewLayout && publishStatus === 'draft' && (
                    <span className="text-[10px] font-medium bg-yellow-100 text-yellow-800 px-2 py-0.5 rounded-full shrink-0">
                      Draft
                    </span>
                  )}
                  {!isNewLayout && publishStatus === 'unpublished_changes' && (
                    <span className="text-[10px] font-medium bg-orange-100 text-orange-800 px-2 py-0.5 rounded-full shrink-0">
                      Unpublished changes
                    </span>
                  )}
                  {!isNewLayout && publishStatus === 'published' && (
                    <span className="text-[10px] font-medium bg-green-100 text-green-800 px-2 py-0.5 rounded-full shrink-0">
                      Published
                    </span>
                  )}
                </div>
                {/* Hide description on mobile */}
                <input
                  type="text"
                  value={layoutDescription}
                  onChange={e => onLayoutDescriptionChange(e.target.value)}
                  placeholder="Add a description..."
                  className="hidden sm:block text-sm text-gray-500 bg-transparent border-none focus:outline-none focus:ring-2 focus:ring-purple-500 rounded px-1 -ml-1 w-64"
                />
              </div>
            </div>
          </div>

          {/* Action Buttons */}
          <div className="flex items-center justify-between sm:justify-end space-x-2 sm:space-x-3">
            {/* Default Layout Toggle */}
            <Tooltip content="Set as Default Layout">
              <label className="flex items-center space-x-1 sm:space-x-2 text-sm text-gray-600 cursor-pointer">
                <input
                  type="checkbox"
                  checked={isDefault}
                  onChange={e => onIsDefaultChange(e.target.checked)}
                  className="w-4 h-4 text-purple-600 border-gray-300 rounded focus:ring-purple-500"
                />
                <span className="hidden sm:inline">Default Layout</span>
                <span className="sm:hidden">Default</span>
              </label>
            </Tooltip>

            {/* Save Button */}
            <button
              onClick={onSave}
              disabled={isSaving || (!hasUnsavedChanges && !isNewLayout)}
              className="btn-primary inline-flex items-center space-x-1 sm:space-x-2 text-sm sm:text-base px-3 sm:px-4"
            >
              {isSaving ? (
                <>
                  <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                  <span className="hidden sm:inline">Saving...</span>
                </>
              ) : (
                <>
                  <Save size={16} className="sm:w-[18px] sm:h-[18px]" />
                  <span className="hidden sm:inline">
                    {isNewLayout ? 'Create Layout' : 'Save Layout'}
                  </span>
                  <span className="sm:hidden">Save</span>
                </>
              )}
            </button>

            {/* Publish Button — shown for existing layouts that aren't fully published */}
            {!isNewLayout && (
              <Tooltip
                content={
                  hasUnsavedChanges
                    ? 'Save first, then publish'
                    : publishStatus === 'published'
                      ? 'Layout is up to date'
                      : 'Publish layout'
                }
              >
                <button
                  onClick={onPublish}
                  disabled={isPublishing || hasUnsavedChanges || publishStatus === 'published'}
                  className="inline-flex items-center space-x-1 sm:space-x-2 text-sm sm:text-base px-3 sm:px-4 py-2 bg-green-600 hover:bg-green-700 text-white rounded-lg transition-colors font-medium disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {isPublishing ? (
                    <>
                      <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                      <span className="hidden sm:inline">Publishing...</span>
                    </>
                  ) : (
                    <>
                      <Rocket size={16} className="sm:w-[18px] sm:h-[18px]" />
                      <span className="hidden sm:inline">Publish</span>
                    </>
                  )}
                </button>
              </Tooltip>
            )}
          </div>
        </div>
      </div>
    </header>
  );
};
